import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function RequestQuotePage() {
  const { tr } = useApp();
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    product: params.get("product") || "",
    product_id: params.get("id") || "",
    quantity: "", message: "",
  });

  useEffect(() => {
    document.title = "Request a Quote | AFC TELECOM";
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone are required."); return; }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: form.name, phone: form.phone, email: form.email,
        message: form.message, product_id: form.product_id || null,
        company: form.company, quantity: form.quantity, product_name: form.product,
        kind: "quote",
      });
      setDone(true);
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
        <h1 className="mt-6 font-display text-3xl font-bold text-[#0B132B]" data-testid="quote-submitted">{tr.quote.submitted}</h1>
        <p className="mt-3 text-slate-600">{tr.contact.sent}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">B2B Inquiry</p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold text-[#0B132B]">{tr.quote.title}</h1>
      <p className="mt-3 text-slate-600 max-w-xl">{tr.quote.sub}</p>

      <form onSubmit={submit} className="mt-10 bg-white border border-slate-200 rounded-3xl p-6 lg:p-10 grid md:grid-cols-2 gap-5" data-testid="quote-form">
        <div>
          <Label>{tr.quote.name} *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" required data-testid="q-name" />
        </div>
        <div>
          <Label>{tr.quote.company}</Label>
          <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" data-testid="q-company" />
        </div>
        <div>
          <Label>{tr.quote.email}</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="q-email" />
        </div>
        <div>
          <Label>{tr.quote.phone} *</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" required data-testid="q-phone" />
        </div>
        <div>
          <Label>{tr.quote.product}</Label>
          <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v, product_id: (products.find(p => p.name === v)?.id) || "" })}>
            <SelectTrigger className="mt-1" data-testid="q-product"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-80">
              {products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{tr.quote.quantity}</Label>
          <Input value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 50 units" className="mt-1" data-testid="q-quantity" />
        </div>
        <div className="md:col-span-2">
          <Label>{tr.quote.message}</Label>
          <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" data-testid="q-message" />
        </div>
        <div className="md:col-span-2">
          <Button type="submit" disabled={submitting} className="w-full md:w-auto rounded-full bg-[#1E5FD9] hover:bg-[#174AB0] px-8 py-6" data-testid="q-submit">
            {submitting ? tr.common.loading : tr.quote.submit}
          </Button>
        </div>
      </form>
    </div>
  );
}
